import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import syncService from '../services/syncService';
import entityMktSettingsService from '../services/partnerMktSettingsService';
import Card from './shared/Card';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import './SyncTaskDetailsPage.css';

const SettingField = ({ setting, value, onChange }) => {
    const inputId = `setting-${setting.settingName}`;

    const renderInput = () => {
        if (setting.dataType === 'SELECT' && setting.availableValues) {
            const options = setting.availableValues.split('|').map(pair => {
                const [key, label] = pair.split(':');
                return { key, label: label || key };
            });
            return (
                <select id={inputId} className="setting-input" value={value || ''} onChange={e => onChange(e.target.value)}>
                    <option value="">Select...</option>
                    {options.map(opt => (
                        <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                </select>
            );
        }
        if (setting.dataType === 'MULTISELECT' && setting.availableValues) {
            const options = setting.availableValues.split('|').map(pair => {
                const [key, label] = pair.split(':');
                return { key, label: label || key };
            });
            const selected = value ? value.split(',') : [];
            const toggle = (key) => {
                const next = selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key];
                onChange(next.join(','));
            };
            return (
                <div className="multiselect-group">
                    {options.map(opt => (
                        <label key={opt.key} className="multiselect-option">
                            <input type="checkbox" checked={selected.includes(opt.key)} onChange={() => toggle(opt.key)} />
                            {opt.label}
                        </label>
                    ))}
                </div>
            );
        }
        if (setting.dataType === 'TEXTAREA') {
            return (
                <textarea id={inputId} className="setting-input setting-textarea" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={setting.placeholder || ''} rows={4} />
            );
        }
        const type = setting.dataType === 'PASSWORD' ? 'password' : setting.dataType === 'NUMBER' ? 'number' : 'text';
        return (
            <input id={inputId} type={type} className="setting-input" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={setting.placeholder || ''} />
        );
    };

    return (
        <div className="setting-field">
            <label className="setting-label" htmlFor={inputId}>{setting.settingLabel}</label>
            {renderInput()}
            {setting.helperText && <p className="setting-helper">{setting.helperText}</p>}
        </div>
    );
};

const SyncTaskDetailsPage = () => {
    const navigate = useNavigate();
    const { syncId, taskId } = useParams();
    const { user } = useAuth();
    const { selectedAccount } = useAccount();

    const [taskDetails, setTaskDetails] = useState(null);
    const [taskSummary, setTaskSummary] = useState([]);
    const [taskErrors, setTaskErrors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [showErrorsSection, setShowErrorsSection] = useState(false);
    const [selectedSummary, setSelectedSummary] = useState(null);
    const [settingValues, setSettingValues] = useState({});
    const [sharedCredentials, setSharedCredentials] = useState([]);
    const [sharedHotelId, setSharedHotelId] = useState(null);
    const [selectedSharedMarketplace, setSelectedSharedMarketplace] = useState(null); // marketplace_id or null
    const originalSettingValues = useRef({});

    useEffect(() => {
        if (syncId && taskId) {
            loadTaskData();
        }
    }, [syncId, taskId]);

    const loadTaskData = async () => {
        try {
            setLoading(true);
            setError(null);

            const detailsData = await syncService.getTaskDetails(syncId, taskId);
            setTaskDetails(detailsData);

            if (detailsData.taskSettings) {
                const initial = {};
                detailsData.taskSettings.forEach(s => { initial[s.settingName] = s.settingValue || ''; });
                setSettingValues(initial);
                originalSettingValues.current = initial;
            }

            if (detailsData.hasSummary) {
                const summaryData = await syncService.getTaskSummary(syncId, taskId);
                setTaskSummary(summaryData || []);
            }
        } catch (err) {
            console.error('Error loading task data:', err);
            setError('Failed to load task details. Please try again.');
        } finally {
            setLoading(false);
        }

        // Load shared credentials in the background after the page is visible
        entityMktSettingsService.getTaskSharedCredentials(syncId)
            .then(sharedData => {
                if (sharedData.credentials && sharedData.credentials.length > 0) {
                    setSharedCredentials(sharedData.credentials);
                    setSharedHotelId(sharedData.hotelId);
                }
            })
            .catch(() => {});
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadTaskData();
        setRefreshing(false);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            const settings = Object.entries(settingValues).map(([settingName, settingValue]) => ({ settingName, settingValue }));
            const role = taskDetails.taskName === 'CONFIGURE SOURCE' ? 'SOURCE' : 'TARGET';
            await syncService.saveTaskSettings(syncId, taskId, settings, role);
            await loadTaskData();
        } catch (err) {
            console.error('Error saving settings:', err);
            setSaveError('Failed to save settings. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleMarkComplete = async () => {
        try {
            setSaving(true);
            setSaveError(null);
            await syncService.updateTask(syncId, taskId, { taskStatus: 'COMPLETE' });
            setTaskDetails(prev => ({ ...prev, taskStatus: 'COMPLETE' }));
        } catch (err) {
            console.error('Error updating status:', err);
            setSaveError('Failed to update status.');
        } finally {
            setSaving(false);
        }
    };

    const handleErrorClick = async (summary) => {
        setSelectedSummary(summary);
        setShowErrorsSection(true);
        try {
            const errorsData = await syncService.getTaskErrors(syncId, taskId);
            setTaskErrors(errorsData || []);
        } catch (err) {
            console.error('Error loading task errors:', err);
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toUpperCase()) {
            case 'COMPLETE': case 'COMPLETED': return 'status-complete';
            case 'RUNNING': return 'status-running';
            case 'ERROR': case 'STOPPED': return 'status-error';
            default: return 'status-pending';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    const getSettingsByGroup = (groupName) =>
        (taskDetails?.taskSettings || [])
            .filter(s => s.settingGroup === groupName)
            .sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

    if (loading) {
        return (
            <div className="sync-task-details-page">
                <div className="sync-task-container">
                    <div className="loading-container"><Spinner size="lg" /><p className="loading-text">Loading task details...</p></div>
                </div>
            </div>
        );
    }

    if (error || !taskDetails) {
        return (
            <div className="sync-task-details-page">
                <div className="sync-task-container">
                    <div className="error-message">{error || 'Task not found'}</div>
                    <Button variant="secondary" onClick={() => navigate(`/sync/${syncId}`)}>
                        <ArrowLeft className="icon" />Back to Project
                    </Button>
                </div>
            </div>
        );
    }

    const handleSelectSharedCredential = (cred) => {
        setSelectedSharedMarketplace(cred.marketplace_id);
        // Pre-populate task settings form: use override_value for must_be_overridden settings,
        // otherwise the decrypted credential value from the provider
        const newValues = { ...originalSettingValues.current };
        cred.settings.forEach(s => {
            const value = s.must_be_overridden === 'Y'
                ? (s.override_value || '')
                : (s.setting_value || '');
            // Match case-insensitively against task setting names
            const matchKey = Object.keys(newValues).find(
                k => k.toLowerCase() === s.setting_name.toLowerCase()
            );
            if (matchKey !== undefined) {
                newValues[matchKey] = value;
            }
        });
        setSettingValues(newValues);
    };

    const handleSelectManualEntry = () => {
        setSelectedSharedMarketplace(null);
        // Clear all fields — shared credential values must not be visible when entering manually
        const cleared = {};
        Object.keys(originalSettingValues.current).forEach(k => { cleared[k] = ''; });
        setSettingValues(cleared);
    };

    const isConnectivity = taskDetails.taskTarget === 'CONNECTIVITY';
    const hasSettings = taskDetails.taskSettings && taskDetails.taskSettings.length > 0;

    return (
        <div className="sync-task-details-page">
            <div className="sync-task-container">
                {/* Header */}
                <Card className="task-header-card">
                    <div className="task-header-top">
                        <div className="task-header-actions">
                            {isConnectivity && taskDetails.taskStatus !== 'COMPLETE' && (
                                <Button variant="primary" onClick={handleSave} disabled={saving}>
                                    {saving ? 'Saving...' : 'Save & Complete'}
                                </Button>
                            )}
                            <button
                                className={`task-status-btn ${getStatusColor(taskDetails.taskStatus)}`}
                                onClick={taskDetails.taskStatus !== 'COMPLETE' ? handleMarkComplete : undefined}
                                disabled={saving || taskDetails.taskStatus === 'COMPLETE'}
                            >
                                {taskDetails.taskStatus || 'PENDING'}
                            </button>
                            <Button variant="secondary" onClick={handleRefresh} disabled={refreshing}>
                                <RefreshCw className={`icon ${refreshing ? 'spinning' : ''}`} />
                            </Button>
                        </div>
                    </div>
                    <div className="task-header-info">
                        <h1 className="task-title">{taskDetails.taskName}</h1>
                        {taskDetails.taskDescription && <p className="task-subtitle">{taskDetails.taskDescription}</p>}
                        {(taskDetails.updateDate || taskDetails.updateUser) && (
                            <p className="task-last-update">
                                Last Update: {formatDate(taskDetails.updateDate)}{taskDetails.updateUser ? ` by ${taskDetails.updateUser}` : ''}
                            </p>
                        )}
                    </div>
                </Card>

                {saveError && <div className="error-banner">{saveError}</div>}

                {/* Shared credentials — only shown for CONNECTIVITY (CONFIG) tasks */}
                {isConnectivity && sharedCredentials.length > 0 && (
                    <Card className="settings-card">
                        <div className="card-header">
                            <h2 className="card-title">Connection Credentials</h2>
                        </div>
                        <div className="settings-form">
                            <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px' }}>
                                Credentials have been shared with this hotel. Select a provider to pre-fill the connection settings below, or enter them manually.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', background: selectedSharedMarketplace === null ? '#f8fafc' : '#fff' }}>
                                    <input type="radio" checked={selectedSharedMarketplace === null} onChange={handleSelectManualEntry} />
                                    <span style={{ fontSize: '13px', color: '#374151' }}>Enter credentials manually</span>
                                </label>
                                {sharedCredentials.map(cred => (
                                    <label key={cred.marketplace_id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', border: `1px solid ${selectedSharedMarketplace === cred.marketplace_id ? '#3b82f6' : '#d1d5db'}`, borderRadius: '6px', cursor: 'pointer', background: selectedSharedMarketplace === cred.marketplace_id ? '#eff6ff' : '#fff' }}>
                                        <input type="radio" checked={selectedSharedMarketplace === cred.marketplace_id} onChange={() => handleSelectSharedCredential(cred)} />
                                        {cred.marketplace_image && <img src={cred.marketplace_image} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />}
                                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>{cred.marketplace_name}</span>
                                        <span style={{ fontSize: '12px', color: '#6b7280' }}>— shared by {cred.source_name || cred.source_type}</span>
                                        {cred.settings.some(s => s.must_be_overridden === 'Y') && (
                                            <span style={{ fontSize: '11px', color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', padding: '1px 8px', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                                                {cred.settings.filter(s => s.must_be_overridden === 'Y').length} field{cred.settings.filter(s => s.must_be_overridden === 'Y').length !== 1 ? 's' : ''} required
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                            {selectedSharedMarketplace !== null && (
                                <p style={{ fontSize: '12px', color: '#059669', marginTop: '10px' }}>
                                    ✓ Settings pre-filled below. Review and click Save &amp; Complete to confirm.
                                </p>
                            )}
                        </div>
                    </Card>
                )}

                {/* Settings sections grouped by taskGroups */}
                {hasSettings && (taskDetails.taskGroups || []).map(group => {
                    const groupSettings = getSettingsByGroup(group.groupName);
                    if (groupSettings.length === 0) return null;
                    return (
                        <Card key={group.groupName} className="settings-card">
                            <div className="card-header">
                                <h2 className="card-title">{group.groupDescription || group.groupName}</h2>
                            </div>
                            <div className="settings-form">
                                {groupSettings.map(setting => (
                                    <SettingField
                                        key={setting.settingName}
                                        setting={setting}
                                        value={settingValues[setting.settingName] ?? setting.settingValue ?? ''}
                                        onChange={val => setSettingValues(prev => ({ ...prev, [setting.settingName]: val }))}
                                    />
                                ))}
                            </div>
                        </Card>
                    );
                })}

                {/* Task Actions (for LOAD/API tasks) */}
                {taskDetails.taskActions && taskDetails.taskActions.length > 0 && (
                    <Card className="actions-card">
                        <div className="card-header">
                            <h2 className="card-title">Actions</h2>
                        </div>
                        <div className="actions-list">
                            {taskDetails.taskActions.map((action, idx) => (
                                <div key={idx} className="action-item">
                                    <div className="action-info">
                                        <div className="action-name">{action.actionName}</div>
                                        {action.actionDescription && <div className="action-desc">{action.actionDescription}</div>}
                                    </div>
                                    <div className="action-right">
                                        <span className={`status-badge ${getStatusColor(action.actionStatus)}`}>{action.actionStatus}</span>
                                        {action.enabled && (
                                            <Button
                                                variant="primary"
                                                disabled={saving}
                                                onClick={async () => {
                                                    setSaving(true);
                                                    try {
                                                        await syncService.updateTask(syncId, taskId, { actionName: action.actionName });
                                                        await loadTaskData();
                                                    } catch (e) {
                                                        console.error(e);
                                                    } finally {
                                                        setSaving(false);
                                                    }
                                                }}
                                            >
                                                Run
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Summary Card */}
                {taskDetails?.hasSummary && (
                    <Card className="summary-card">
                        <div className="card-header">
                            <h2 className="card-title">Task Summary</h2>
                        </div>
                        {taskSummary.length === 0 ? (
                            <div className="empty-state-inline"><p>No summary data available yet.</p></div>
                        ) : (
                            <div className="table-container">
                                <table className="summary-table">
                                    <thead>
                                        <tr>
                                            <th>Scope</th>
                                            <th className="text-right">Total</th>
                                            <th className="text-right">Pending</th>
                                            <th className="text-right">Processing</th>
                                            <th className="text-right">Errors</th>
                                            <th className="text-right">Skipped</th>
                                            <th className="text-right">Complete</th>
                                            <th className="text-right">Progress</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {taskSummary.map((summary, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <div className="scope-cell">
                                                        {summary.scopeiconurl && (
                                                            <img src={summary.scopeiconurl} alt={summary.scopetype} className="scope-icon" onError={e => { e.target.style.display = 'none'; }} />
                                                        )}
                                                        <div className="scope-info">
                                                            <div className="scope-name">{summary.scopedescription}</div>
                                                            <div className="scope-type">{summary.scopetype}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-right text-gray">{summary.records}</td>
                                                <td className="text-right text-yellow">{summary.pending}</td>
                                                <td className="text-right text-blue">{summary.processing}</td>
                                                <td className="text-right">
                                                    {summary.error > 0 ? (
                                                        <button onClick={() => handleErrorClick(summary)} className="error-button">
                                                            <AlertCircle className="error-icon" />{summary.error}
                                                        </button>
                                                    ) : <span className="text-gray">{summary.error}</span>}
                                                </td>
                                                <td className="text-right text-gray">{summary.skipped}</td>
                                                <td className="text-right text-green">{summary.complete}</td>
                                                <td className="text-right">
                                                    <div className="progress-cell">
                                                        <div className="progress-bar">
                                                            <div className="progress-fill" style={{ width: `${parseFloat(summary.completePercent)}%` }} />
                                                        </div>
                                                        <span className="progress-text">{parseFloat(summary.completePercent).toFixed(1)}%</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                )}

                {/* Errors Section */}
                {showErrorsSection && selectedSummary && (
                    <Card className="errors-card">
                        <div className="card-header">
                            <h2 className="card-title">Errors for {selectedSummary.scopedescription}</h2>
                            <Button variant="secondary" onClick={() => setShowErrorsSection(false)}>Close</Button>
                        </div>
                        {taskErrors.length === 0 ? (
                            <div className="empty-state-inline"><p>No error details available.</p></div>
                        ) : (
                            <div className="errors-list">
                                {taskErrors.map((err, index) => (
                                    <div key={index} className="error-item">
                                        <div className="error-header">
                                            <AlertCircle className="error-icon-large" />
                                            <div className="error-info">
                                                <div className="error-title">{err.errorType || 'Error'}</div>
                                                <div className="error-timestamp">{err.errorDate ? new Date(err.errorDate).toLocaleString() : ''}</div>
                                            </div>
                                        </div>
                                        {err.errorMessage && <div className="error-message-block">{err.errorMessage}</div>}
                                        {err.errorDetails && <div className="error-details-block"><strong>Details:</strong> {err.errorDetails}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
};

export default SyncTaskDetailsPage;
